import React, { useState } from 'react';
import { Search, Download, Edit, Plus, FileText, MapPin, Camera, Clipboard, ArrowLeft, Trash2, Edit2, Save, X, ChevronUp, ChevronDown } from 'lucide-react';
import PortalBackground from '../components/PortalBackground';

const ExistingConditionsTemplate = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [steps, setSteps] = useState([
    {
      id: '1',
      stepName: 'Site Arrival & Safety Check',
      order: 1,
      description: 'Initial site arrival procedures and safety assessment',
      checklist: ['Safety equipment check', 'Site access verification', 'Weather conditions assessment'],
      notes: 'Ensure all safety protocols are followed before beginning any documentation.'
    },
    {
      id: '2',
      stepName: 'Exterior Documentation',
      order: 2,
      description: 'Document all exterior elements and conditions',
      checklist: ['Photograph all sides of building', 'Document roof condition', 'Assess drainage systems', 'Note landscaping features'],
      notes: 'Take photos from multiple angles and document any visible damage or wear.'
    },
    {
      id: '3',
      stepName: 'Interior Room-by-Room Survey',
      order: 3,
      description: 'Systematic interior documentation of each room',
      checklist: ['Measure room dimensions', 'Document wall conditions', 'Assess flooring', 'Note electrical outlets', 'Check HVAC vents'],
      notes: 'Use consistent measurement methods and document any existing damage or modifications.'
    },
    {
      id: '4',
      stepName: 'Structural Assessment',
      order: 4,
      description: 'Evaluate structural integrity and foundation',
      checklist: ['Check foundation for cracks', 'Assess load-bearing walls', 'Document ceiling conditions', 'Note any sagging or bowing'],
      notes: 'Pay special attention to any signs of structural issues or previous repairs.'
    },
    {
      id: '5',
      stepName: 'Mechanical Systems Review',
      order: 5,
      description: 'Document HVAC, electrical, and plumbing systems',
      checklist: ['Document HVAC equipment', 'Assess electrical panel', 'Check plumbing fixtures', 'Note any code violations'],
      notes: 'Document model numbers, ages, and conditions of all mechanical systems.'
    }
  ]);
  const [editingStep, setEditingStep] = useState(null);
  const [editedContent, setEditedContent] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);

  const handleAddStep = () => {
    const newStep = {
      id: Date.now().toString(),
      stepName: 'New Step',
      order: steps.length + 1,
      description: '',
      checklist: [],
      notes: ''
    };
    setSteps([...steps, newStep]);
    setEditingStep(newStep.id);
    setEditedContent({ ...newStep });
  };

  const handleEditStep = (step) => {
    setEditingStep(step.id);
    setEditedContent({ ...step });
  };

  const handleDeleteStep = (stepId) => {
    const updatedSteps = steps
      .filter(step => step.id !== stepId)
      .map((step, index) => ({ ...step, order: index + 1 }));
    setSteps(updatedSteps);
  };

  const handleSaveStep = () => {
    if (editingStep && editedContent) {
      const updatedSteps = steps.map(step =>
        step.id === editingStep ? editedContent : step
      );
      setSteps(updatedSteps);
      setEditingStep(null);
      setEditedContent(null);
    }
  };

  const handleMoveStep = (stepId, direction) => {
    const currentIndex = steps.findIndex(s => s.id === stepId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < steps.length) {
      const updatedSteps = [...steps];
      [updatedSteps[currentIndex], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[currentIndex]];
      
      // Update orders
      const finalSteps = updatedSteps.map((step, index) => ({ ...step, order: index + 1 }));
      setSteps(finalSteps);
    }
  };

  const toggleStep = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  const handleUpdateField = (field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddChecklistItem = () => {
    setEditedContent(prev => ({
      ...prev,
      checklist: [...prev.checklist, "New checklist item"]
    }));
  };

  const handleDeleteChecklistItem = (index) => {
    setEditedContent(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateChecklistItem = (index, value) => {
    setEditedContent(prev => ({
      ...prev,
      checklist: prev.checklist.map((item, i) => 
        i === index ? value : item
      )
    }));
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setEditedContent(null);
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">Existing Conditions Templates</h1>
          <p className="text-gray-600">Access and manage site survey and existing conditions documentation templates</p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <button className="bg-red-600 text-white p-2 rounded-md hover:bg-red-700 flex items-center justify-center">
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Steps List */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h2 className="text-lg font-medium text-gray-900">Documentation Steps</h2>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={handleAddStep}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Step
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleMoveStep(step.id, 'up')}
                        disabled={step.order === 1}
                        className={`p-1 rounded ${
                          step.order === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleMoveStep(step.id, 'down')}
                        disabled={step.order === steps.length}
                        className={`p-1 rounded ${
                          step.order === steps.length
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <ChevronDown className="h-5 w-5" />
                      </button>
                      <span className="text-sm font-medium text-gray-900">
                        {step.stepName}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditStep(step)}
                        className="p-1 text-gray-600 hover:text-red-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteStep(step.id)}
                        className="p-1 text-gray-600 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleStep(step.id)}
                        className="p-1 text-gray-600 hover:text-gray-900"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transform transition-transform ${
                            expandedStep === step.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {expandedStep === step.id && (
                    <div className="p-4 border-t border-gray-200">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={step.stepName}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={step.description}
                            readOnly
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Checklist Items
                          </label>
                          <div className="space-y-2">
                            {step.checklist.map((item, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2"
                              >
                                <input
                                  type="text"
                                  value={item}
                                  readOnly
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notes
                          </label>
                          <textarea
                            value={step.notes}
                            readOnly
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Step Modal */}
        {editingStep && editedContent && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-[600px] max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-center w-full">Edit Step</h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-500 absolute right-6"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editedContent.stepName}
                    onChange={(e) => handleUpdateField('stepName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editedContent.description}
                    onChange={(e) => handleUpdateField('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Checklist Items
                    </label>
                    <button
                      onClick={handleAddChecklistItem}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editedContent.checklist.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) =>
                            handleUpdateChecklistItem(index, e.target.value)
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <button
                          onClick={() => handleDeleteChecklistItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editedContent.notes}
                    onChange={(e) => handleUpdateField('notes', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveStep}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => {
              console.log('Saving steps:', steps);
            }}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExistingConditionsTemplate; 